import { useCallback, useEffect, useState } from 'react'

import { RequestStatus } from 'core/domain/Request'
import { useAbortController } from 'core/ui/hooks/useAbortController'

import type { ShipmentAccountingSummary } from '../../domain/ShipmentCharges'
import { getShipmentAccountingSummary } from '../../use-cases/getShipmentAccountingSummary'

type ShipmentChargesState = {
  summary?: ShipmentAccountingSummary
  summaryStatus: RequestStatus
}

const initialShipmentChargesState = {
  summaryStatus: RequestStatus.Initial,
}

export function useShipmentAccountingSummary(shipmentUUID: string) {
  const abortController = useAbortController([shipmentUUID])

  const [{ summary, summaryStatus }, setShipmentChargesState] = useState<ShipmentChargesState>(
    initialShipmentChargesState
  )

  useEffect(() => {
    async function fetchSummary() {
      try {
        setShipmentChargesState(previous => ({
          ...previous,
          summaryStatus: RequestStatus.Pending,
        }))

        const response = await getShipmentAccountingSummary(shipmentUUID, {
          signal: abortController.signal,
        })

        setShipmentChargesState(previous => ({
          ...previous,
          summary: response,
          summaryStatus: RequestStatus.Done,
        }))
      } catch (error) {
        if (!abortController.signal.aborted) {
          setShipmentChargesState(previous => ({
            ...previous,
            summary: undefined,
            summaryStatus: RequestStatus.Error,
          }))
        }
      }
    }

    fetchSummary()
  }, [abortController.signal, shipmentUUID])

  const refetchSummary = useCallback(async () => {
    try {
      setShipmentChargesState(previous => ({
        ...previous,
        summaryStatus: RequestStatus.Pending,
      }))

      const response = await getShipmentAccountingSummary(shipmentUUID)

      setShipmentChargesState(previous => ({
        ...previous,
        summary: response,
        summaryStatus: RequestStatus.Done,
      }))
    } catch (error) {
      setShipmentChargesState(previous => ({
        ...previous,
        summary: undefined,
        summaryStatus: RequestStatus.Error,
      }))
    }
  }, [shipmentUUID])

  return { summary, summaryStatus, refetchSummary }
}
